import { giphyLocalUrl } from '@/app/util/env'
import { Interface, NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { getCookie, getPost } from '@/util/url'
import { CSRF_COOKIE_NAME } from '@/util/user'
import { useState } from 'react'
import { useFormState } from 'react-dom'
import Button from '../button'

const REASONS_DESCRIPTION = {
    animal_welfare: 'Animal welfare',
    pornography: 'Pornography/Nudity',
    violence: 'Violence',
    hate: 'Hate',
    misinformation: 'Misinformation',
    bullying_harassment: 'Bullying and harassment',
    threatening_stalking: 'Threatening or stalking',
    private_information: 'Includes private information',
    terrorism: 'Terrorism and violent extremists',
    illegal_activity: 'Illegal content: Illegal activity',
    spam: 'Spam',
    self_harm: 'Promoting self-harm',
    underage_user: 'Underage user',
    graphic_medical_content: 'Graphic medical content',
    disasters_tragedies: 'Disasters and tragedies',
    clips_copyright: 'Clips Copyright',
    coordinated_harm: 'Coordinated harm or threat to life',
    csam: 'CSAM',
    illegal_substances: 'Illegal content: Selling illegal substances/service',
    nonconsensual_image: 'Non-consensual image',
    nonconsensual_deepfake: 'Non-consensual deepfake',
    bullying_harassment_women: 'Bullying and harassment of women',
    threatening_stalking_women: 'Threatening or stalking of women',
    incitement_violence_women: 'Incitement to violence against women',
    discrimination: 'Discrimination',
    copyright: 'Copyright infringement',
    trademark: 'Trademark violation',
    csam_deepfake: 'CSAM deepfake',
    csam_jokes: 'CSAM jokes or mockery',
    public_health: 'Public health',
    eating_disorder: 'Eating disorder',
    mutilation: 'Mutilation',
    solicitation: 'Solicitation',
    incitement_violence_hate: 'Incitement to violence or hate',
    sexual_violence: 'Sexual violence',
    perpetrators: 'Perpetrators of sexual violence',
    school_shootings: 'School shootings',
    epilepsy: 'Epilepsy',
    nudity: 'Nudity',
    real_gore: 'Real gore',
} as const

type Reason = keyof typeof REASONS_DESCRIPTION
const REASONS_ORDER: Reason[] = [
    'animal_welfare',
    'pornography',
    'violence',
    'clips_copyright',
    'hate',
    'misinformation',
    'bullying_harassment',
    'threatening_stalking',
    'spam',
    'self_harm',
    'underage_user',
    'graphic_medical_content',
    'disasters_tragedies',
    'private_information',
    'csam',
    'illegal_activity',
    'coordinated_harm',
    'terrorism',
    'illegal_substances',
    'nonconsensual_image',
    'nonconsensual_deepfake',
    'bullying_harassment_women',
    'threatening_stalking_women',
    'incitement_violence_women',
    'discrimination',
    'copyright',
    'trademark',
    'csam_deepfake',
    'csam_jokes',
    'public_health',
    'eating_disorder',
    'mutilation',
    'solicitation',
    'incitement_violence_hate',
    'animal_welfare',
    'perpetrators',
    'school_shootings',
    'epilepsy',
    'nudity',
    'real_gore',
]

type Props = {
    onClose: () => void
    gifIds: (string | number)[]
}

export const contentTakedown = (
    gifIds: (string | number)[],
    reason: string,
    additionalInfo: string,
    shouldEmailUser: boolean
) => {
    const params = {
        takedowns: gifIds.map((gifId) => ({
            reason: reason,
            gif_id: gifId,
        })),
        send_email: shouldEmailUser,
        note: additionalInfo || undefined,
    }
    return fetch(`${giphyLocalUrl}/api/v3/content-takedown/takedown`, {
        ...getPost(getCookie(CSRF_COOKIE_NAME), false),
        body: JSON.stringify(params),
    })
}

const reasonInput = 'reason'
const noteInput = 'note'
const sendEmailName = 'sendemail'

const ContentTakedown = ({ onClose, gifIds }: Props) => {
    const [isPending, setPending] = useState(false)
    const submitClicked = async (_: string, queryData: FormData) => {
        setPending(true)
        try {
            await contentTakedown(
                gifIds,
                queryData.get(reasonInput) as string,
                queryData.get(noteInput) as string,
                queryData.get(sendEmailName) === 'on'
            )
        } catch (error) {
            if (error instanceof Error) {
                console.error(error.message)
                return error.message
            } else {
                throw error
            }
        } finally {
            setPending(false)
        }
        return 'Success'
    }
    const [state, formAction] = useFormState(submitClicked, '')

    return state === 'Success' ? (
        <div className={`flex justify-center p-20 text-center text-lg ${NexaBlack.variable}`}>Gif deleted.</div>
    ) : (
        <form
            className={cn(
                `group relative m-4 flex flex-col gap-2 ${Interface.variable}`,
                isPending && 'pointer-events-none opacity-80'
            )}
            action={formAction}
        >
            <h1 className="text-lg font-bold">Why are you removing the selected content?</h1>
            <div className="mb-4 grid grid-cols-4 gap-4">
                {REASONS_ORDER.slice().map((key) => (
                    <div className="flex items-start gap-2" key={key}>
                        <input type="radio" id={key} value={key} name={reasonInput} required />
                        <label className="select-none" htmlFor={key}>
                            {REASONS_DESCRIPTION[key]}
                        </label>
                    </div>
                ))}
            </div>
            <h1 className="text-lg font-bold">Any additional information?</h1>
            <textarea className="my-1 h-20 w-full resize-none" name={noteInput} />
            <div className="my-4 flex gap-2">
                <input type="checkbox" name={sendEmailName} id={sendEmailName} />{' '}
                <label htmlFor={sendEmailName}>Send email to user</label>
            </div>
            <div className="flex flex-col justify-center gap-2">
                <Button
                    type="submit"
                    className="bg-gradient-primary cursor-pointer group-invalid:pointer-events-none group-invalid:opacity-60"
                >
                    Submit
                </Button>
                <Button onClick={onClose} className="bg-giphyDarkCharcoal">
                    Cancel
                </Button>
            </div>
        </form>
    )
}

export default ContentTakedown
